import { useAppSelector } from '@/hooks/redux';

import { selectYourFinancesFeature } from '@/redux/configs/baseSelectors';
import { selectUser } from '@/redux/user-data';

import { DocumentStatus, SigningFormType } from '@/types/document';
import { FetchedFinancialInfo, FinancialType } from '@/types/finance';
import { User } from '@/types/user';

import { isIndividualUser } from '@/utils/user.ts';

import { useFinancialInfo } from './useFinancialInfo';

export enum FinanceQuestion {
  Assets = 1,
  Liabilities = 2,
  IncomeAndSpending = 3,
  Complete = 4,
}

export type FinancialState = {
  isComplete: boolean;
  currentQuestion: FinanceQuestion;
  isLoading: boolean;
};

export type YourFinancesStatus = {
  isVisible: boolean;
} & FinancialState;

export function useYourFinancesStatus(): YourFinancesStatus {
  const isYourFinancesEnabled = useAppSelector(selectYourFinancesFeature);
  const user = useAppSelector(selectUser);
  const { data, isLoading } = useFinancialInfo();

  const currentQuestion = getCurrentQuestion(user, data);

  const isVisible =
    !!user?.isHighRisk &&
    isYourFinancesEnabled &&
    isIndividualUser(user) &&
    !hasLegacySignedAML10(user);

  return {
    isVisible,
    isLoading,
    isComplete: currentQuestion === FinanceQuestion.Complete,
    currentQuestion,
  };
}

const hasLegacySignedAML10 = (user: User): boolean => {
  // check if user has signed AML10 document with status InReview or Approved
  // and has no trace of financial info by simply checking if `hasLiability` is set
  // (legacy user)
  return (
    user.documents.some(
      document =>
        document.docType === SigningFormType.AML10 &&
        [DocumentStatus.InReview, DocumentStatus.Approved].includes(
          document.status,
        ),
    ) && typeof user.hasLiability !== 'boolean'
  );
};

const getCurrentQuestion = (
  user?: User,
  financialInfo?: FetchedFinancialInfo[],
): FinanceQuestion => {
  const hasAssets =
    financialInfo?.some(item => item.financialType === FinancialType.Asset) ??
    false;

  if (!hasAssets) {
    return FinanceQuestion.Assets;
  }

  const hasLiabilities =
    user?.hasLiability &&
    financialInfo?.some(item => item.financialType === FinancialType.Liability);

  // If user has no liability, we consider the question as provided to skip it
  const isLiabilityProvided = user?.hasLiability === false || !!hasLiabilities;

  if (!isLiabilityProvided) {
    return FinanceQuestion.Liabilities;
  }

  const hasIncomeAndSpending =
    user.totalMonthlyIncome &&
    user.totalMonthlySpending &&
    user.sourceOfIncome &&
    user.howOftenPaid;
  if (!hasIncomeAndSpending) {
    return FinanceQuestion.IncomeAndSpending;
  }
  return FinanceQuestion.Complete;
};
